/**
 * TableMultiSelectionLogic.ts - Pure logic for table multi-selection state.
 *
 * These functions contain the value-only computations that were previously
 * embedded in the `setMultiIndices` / `saveMultiSelect` instance methods on the
 * `TableEditable` class component. They take primitives / plain state and
 * return plain objects: no store access, no DOM, no React.
 *
 * The store writes (setState / storeState / setMultiEditing) and DOM focus that
 * used to surround these computations live in the callers: the store-native
 * table mutations built by `createTableMutations` and, during the migration, the `TableEditable` class.
 */

import * as MultiSelectListUtil from 'notebook/mediocre/selection/block/MultiSelectListUtil';
import {SelectionRestoreData} from 'notebook/mediocre/selection/SelectionStore';
import type {Cell} from 'notebook/tables/constants/CellConstants';
import type {Table} from 'notebook/tables/constants/TableConstants';
import {TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import type {TableEditableState} from 'notebook/tables/core-ui/TableEditableState';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
/**
 * The subset of table state written when the multi-selection indices change.
 */
export type MultiIndicesStatePatch = Pick<
  TableEditableState,
  'multiStartIndex' | 'multiEndIndex' | 'multiSelIndex' | 'multiActualIndex'
>;

/**
 * Compute the state patch produced by a `setMultiIndices` call.
 *
 * `multiSelIndex` / `multiActualIndex` default to the current selected index
 * (when provided) and otherwise collapse onto the selection's `start` anchor.
 *
 * @param start - The anchor index the selection starts on
 * @param end - The index the selection ends on
 * @param currentMultiSelIndex - The currently selected index within the selection, or null
 * @returns The partial state to write to the store
 */
export function computeMultiIndicesState(
  {start, end}: {start: TableIndex; end: TableIndex},
  currentMultiSelIndex: TableIndex | null
): MultiIndicesStatePatch {
  const selectedIndex = currentMultiSelIndex != null ? currentMultiSelIndex : start;
  return {
    multiStartIndex: start,
    multiEndIndex: end,
    multiSelIndex: selectedIndex,
    multiActualIndex: selectedIndex,
  };
}

/**
 * Compute the undo/redo restore metadata for a selection.
 *
 * Thin, table-typed wrapper over `MultiSelectListUtil.computeState` so table
 * code depends on this logic module rather than reaching into the shared
 * mediocre selection util directly.
 *
 * @param state - The selection state (or an explicit `{selection}`) to snapshot
 * @param multiSelectKey - The table's multi-select key
 * @returns The restore metadata to push onto the history
 */
export function computeSelectionRestoreMetadata(
  state: Partial<MultiSelectListUtil.MultiSelectState<TableIndex>> | {selection: SelectionRestoreData},
  multiSelectKey: string
): MultiSelectListUtil.MetadataForRestore<TableIndex> {
  return MultiSelectListUtil.computeState(state, multiSelectKey);
}

/**
 * Look up the cell at an index, returning null when the row or cell is absent.
 */
function getCellValue(table: Table, index: TableIndex): Cell | null {
  const [i, j] = index;
  const tableRow = table.table[i];
  if (tableRow == null) {
    return null;
  }
  return tableRow[j] ?? null;
}

/**
 * The text of the cell at `index`, or the empty string when the cell has none.
 *
 * Pure derivation from the table; mirrors the former `getMultiEditingText`
 * instance method on `TableEditable`.
 */
export function getMultiEditingText(table: Table, index: TableIndex): string {
  const cell = getCellValue(table, index);
  return cell != null && cell.text != null ? cell.text : '';
}

/**
 * The typed value of the cell at `index`, or null when the cell has none.
 *
 * Pure derivation from the table; mirrors the former `getMultiEditingTypedValue`
 * instance method on `TableEditable`.
 */
export function getMultiEditingTypedValue(table: Table, index: TableIndex): TypedValue | null {
  const cell = getCellValue(table, index);
  return cell != null && cell.typedValue != null ? cell.typedValue : null;
}
